// What the site's players play, in the shape the trackers take (PlayableContent in
// playback.js), built from the API's data:
// - library versions: locales.<lang> in the library response (id, start_time, end_time);
// - videos in the member's playlist: playlistItem.video - the same versions, with start/end;
// - the floating audio player's tracks: /audios/playlist (id, musicSrc).
// Each returns null when there is no usable id, and nothing is tracked then.

/** A library version - locales.<lang> - played as a "video" or an "audio" item. */
export function libraryContent(type, version) {
  return content(type, version?.id, version?.start_time, version?.end_time);
}

/** A video from the member's playlist (playlistItem.video). */
export function playlistVideoContent(video) {
  return content("video", video?.id, video?.start, video?.end);
}

/** A track of the floating audio player (/audios/playlist): numbered apart from the library. */
export function podcastContent(track) {
  const id = toId(track?.id);

  return id === null ? null : { type: "podcast", id };
}

/**
 * For useAudioPlayerActivity(): finds which of `tracks` - react-jinke-music-player's audio
 * list, by `musicSrc` - an <audio> element is playing, and returns `toContent(track)` for it.
 * A musicSrc that is a function (resolved when the track loads, see secureMusicSrc) is
 * matched by the link it keeps once resolved.
 */
export function findTrackBySrc(tracks, toContent) {
  const bySrc = new Map();
  tracks.forEach((track) => {
    const src = sameUrl(track.musicSrc);
    if (src !== null) {
      bySrc.set(src, track);
    }
  });

  return (media) => {
    const src = sameUrl(media.currentSrc);
    const track =
      bySrc.get(src) ??
      (src === null
        ? undefined
        : tracks.find((item) => typeof item.musicSrc === "function" && sameUrl(item.musicSrc.link) === src));

    return track === undefined ? null : toContent(track) ?? null;
  };
}

function content(type, id, start, end) {
  const contentId = toId(id);
  if (contentId === null) {
    return null;
  }

  return { type, id: contentId, startSeconds: toSeconds(start), endSeconds: toSeconds(end) };
}

/** The API's ids are positive integers - sometimes sent as text. */
function toId(value) {
  const id = typeof value === "string" ? Number(value) : value;

  return Number.isInteger(id) && id > 0 ? id : null;
}

/** Seconds into the media, or null when there are none. */
function toSeconds(value) {
  const seconds = typeof value === "string" && value.trim() !== "" ? Number(value) : value;

  return typeof seconds === "number" && Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

/** URLs in one form: the browser reports currentSrc percent-encoded, the API may send spaces. */
function sameUrl(src) {
  if (typeof src !== "string" || src === "") {
    return null;
  }

  try {
    return new URL(src, window.location.href).href;
  } catch {
    return null;
  }
}
